import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import * as _ from 'lodash';

import { IApplicationState } from '../../../reducers';
import { getQuestionByQuestionId } from '../../../services/question.service';
import {
    QTYPES,
    ProjectLanguage,
    Question,
    AudienceRule as IAudienceRule,
    AudienceRuleOption,
    MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES,
    MULTIPLE_ANSWER_AUDIENCE_RULE_MODE,
    QuestionSummary,
    Project,
    PlainTextContent
} from '@nexxt/common/types';
import Loader from '../../common/Loader/Loader';
import {
    getIconScaleOptions,
    getVectorMapOptions
} from '../../../utils/question';
import {
    MULTIPLE_ANSWER_TYPES,
    SYSTEM_METADATA_QTYPES
} from '../../../constants/question';
import {
    NA_OPTION_VALUE,
    BUILTIN_OPTION_ID,
    NA_DEFAULT_TEXT,
    BUILT_IN_OPTION_VALUE,
    OTHER_OPTION_VALUE,
    OTHER_DEFAULT_TEXT,
    DEVICE_OPTION_VALUE_PREFIX,
    NUMERIC_BEFORE_RANGE_VALUE,
    NUMERIC_AFTER_RANGE_VALUE
} from '@nexxt/common/constants';
import { getDemographicDetails } from '@nexxt/common/demographics/index';
import { AUDIENCE_RULE_TYPES } from '../../../interface/project.interface';
import MultipleAudienceRule from './AudienceRuleType/MultipleAudienceRule';
import ValidationService from '@nexxt/common/services/ValidationService';
import SingleAudienceRule from './AudienceRuleType/SingleAudienceRule';
import BuiltInAudienceRule from './AudienceRuleType/BuiltInAudienceRule';
import { DYNATA_QVALUE } from '@nexxt/common/demographics/types';
import NumericAudienceRule from './AudienceRuleType/NumericAudienceRule';

export interface AudienceRuleProps {
    index: number;
    keyName: AUDIENCE_RULE_TYPES;
    audienceRule: IAudienceRule;
    questionList: QuestionSummary[] | Question[];
    filteredQuestionList: QuestionSummary[] | Question[];
    currentProject: Project;
    editEnabled: boolean;
    languages: ProjectLanguage[];
    health?: any;
    totalSampleSize: number;
    validationService: ValidationService;
    onEdit: (index: number) => void;
    onCancel: (index: number) => void;
    onSave: (index?: number) => void;
    onDelete: (index: number) => void;
    onRuleUpdate: (
        index: number,
        audienceRule: IAudienceRule,
        callback?: () => void
    ) => void;
    getQuestionByQuestionId: (id: number, options: any) => Promise<Question>;
    onAddDynamicQuestion: (
        selectedQuestion: any,
        audienceRuleOptions: AudienceRuleOption[],
        keyname: AUDIENCE_RULE_TYPES
    ) => void;
    onUpdateDynamicQuestion: (
        audienceRuleOptions: AudienceRuleOption[],
        keyname: AUDIENCE_RULE_TYPES,
        index: number
    ) => void;
    disableByFieldName: (keyName?: string) => boolean;
}

export interface errorObject {
    errorMessage: string;
    optionKeys: { [optionKey: number]: boolean };
    ageRange?: any[];
}
export interface AudienceRuleState {
    isLoading: boolean;
    isNew: boolean;
    isExpandedAudienceRule: boolean;
    showError: boolean;
    hasInvalid: boolean;
    errorObject: errorObject;
    isMenuOpen: boolean;
    numericOptions: {
        rangeOptions: AudienceRuleOption[];
        beforeRangeOption: AudienceRuleOption;
        afterRangeOption: AudienceRuleOption;
        notApplicableOption: AudienceRuleOption;
    };
    selectedQuestionSummary: QuestionSummary;
}

const L = [];
const O = {};
class AudienceRule extends React.Component<
    AudienceRuleProps,
    AudienceRuleState
> {
    _numericOptions;
    myRef: React.RefObject<HTMLDivElement>;
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            isExpandedAudienceRule:
                this.props.audienceRule.audienceRuleOptions.some(
                    (opt) => opt.id
                )
                    ? false
                    : true,
            isNew: this.props.audienceRule.audienceRuleOptions.some(
                (opt) => opt.id
            )
                ? false
                : true,
            hasInvalid: false,
            showError: this.props.index === -1 ? false : true,
            errorObject: null,
            isMenuOpen: false,
            numericOptions: {
                rangeOptions: [],
                beforeRangeOption: null,
                afterRangeOption: null,
                notApplicableOption: null
            },
            selectedQuestionSummary: null
        };
        this.myRef = React.createRef();
    }
    async componentDidMount() {
        // For when new screener is created, scroll to element
        if (!this.state.isLoading && this.props.index === -1) {
            this.onScrollToView();
        }
        // For the existing custom screener
        this.props.audienceRule.selectedQuestion &&
            this.props.audienceRule.audienceRuleOptions.length === 0 &&
            this.onQuestionChange(this.props.audienceRule.selectedQuestion);

        this.props.audienceRule.selectedQuestion.type === QTYPES.NUMERIC &&
            this.initializeNumericOptions(this.props.audienceRule);
        this.initializeSelectedQuestionSummary();
    }

    componentDidUpdate(
        prevProps: Readonly<AudienceRuleProps>,
        prevState: Readonly<AudienceRuleState>
    ): void {
        if (!_.isEqual(prevState.numericOptions, this.state.numericOptions)) {
            this.addNumericToAudienceRule();
        }

        if (!_.isEqual(prevProps.questionList, this.props.questionList)) {
            this.initializeSelectedQuestionSummary();
        }
    }

    onScrollToView = () => {
        setTimeout(() => {
            this.myRef.current.scrollIntoView();
        }, 400);
    };

    onSampleSizeChange = (
        id: number,
        index: number,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        // Currently making min and max the same value
        const eName = event.target.name === 'max' ? 'min' : 'max';
        const currentAudienceRuleOption = {
            ...this.props.audienceRule.audienceRuleOptions[index]
        };
        currentAudienceRuleOption.sampleSize = {
            ...currentAudienceRuleOption.sampleSize,
            [event.target.name]: parseInt(event.target.value),
            [eName]: parseInt(event.target.value)
        };
        const audienceRule = { ...this.props.audienceRule };
        audienceRule.audienceRuleOptions.splice(
            index,
            1,
            currentAudienceRuleOption
        );
        this.props.onRuleUpdate(this.props.index, audienceRule, this.isValid);
    };

    initializeSelectedQuestionSummary = () => {
        const { questionList, audienceRule } = this.props;
        const { selectedQuestion } = audienceRule;
        const selectedQuestionSummary = (
            questionList as QuestionSummary[]
        )?.find((q) => q?.id === selectedQuestion.questionId);

        this.setState({ selectedQuestionSummary });
    };
    /**
     * Get question option as the selected question is changed
     * if questionID > 0, determine it is builtIn(question.builtInID) question and get question details
     * if questionID < 0 --> built-in demographical question
     */
    onQuestionChange = async (question: any) => {
        if (this.state.isLoading) {
            return;
        }
        this.setState({
            isLoading: true
        });

        const retrievedAudienceRuleOptions = await this.getAudienceOptions(
            question,
            true,
            question.type as QTYPES
        );
        const existingAudienceRuleOptionIds =
            this.props.audienceRule.audienceRuleOptions.map(
                (rule) => rule.optionId
            );
        const filteredAudienceRuleOptions = retrievedAudienceRuleOptions.filter(
            (opt) => !existingAudienceRuleOptionIds.includes(opt.optionId)
        );
        const audienceRuleOptions = [
            ...this.props.audienceRule.audienceRuleOptions,
            ...filteredAudienceRuleOptions
        ];
        this.initializeSelectedQuestionSummary();
        this.setState({
            isLoading: false
        });
        this.props.onRuleUpdate(
            this.props.index,
            {
                ...this.props.audienceRule,
                selectedQuestion: question,
                audienceRuleOptions
            },
            this.isValid
        );
    };

    getAudienceOptions = async (
        question: any,
        isOriginalQuestion = true,
        originalQuestionType?: QTYPES
    ): Promise<AudienceRuleOption[]> => {
        let options = L,
            audienceRuleOptions = L;
        let mode: any = {};

        if (question.questionId > 0) {
            const fullQuestionData: Question = await this.getQuestion(
                question.questionId
            );
            options = fullQuestionData.options || L;
            const isBuiltIn = !!fullQuestionData.builtInID;
            if (isBuiltIn && options.length === 0) {
                const countryValue =
                    this.props.currentProject.countries[0].value;
                const builtinQuestion =
                    isBuiltIn &&
                    getDemographicDetails(
                        this.props.languages,
                        fullQuestionData.builtInID,
                        countryValue
                    );
                options = builtinQuestion?.options || [];
            }
            switch (fullQuestionData.type) {
                case QTYPES.VECTOR_MAP:
                    options = getVectorMapOptions(fullQuestionData);
                    break;
                case QTYPES.ICONS:
                    options = getIconScaleOptions(fullQuestionData);
                    break;
                case QTYPES.MULTIPLE_CHOICE:
                case QTYPES.RANK_ORDER:
                    // if the piping reference question is multiple choice but the original question is single choice
                    if (
                        [QTYPES.MULTIPLE_CHOICE, QTYPES.RANK_ORDER].includes(
                            originalQuestionType
                        )
                    )
                        mode = {
                            ruleType:
                                MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT,
                            ruleMode: MULTIPLE_ANSWER_AUDIENCE_RULE_MODE.ANY
                        };
                    break;
                default:
                    break;
            }

            /** Add not applicable options */
            if (
                isOriginalQuestion &&
                fullQuestionData.showNotApplicableOption
            ) {
                const naOption = {
                    label:
                        (
                            fullQuestionData.notApplicableTexts?.[0]
                                ?.contents?.[0] as PlainTextContent
                        )?.text || NA_DEFAULT_TEXT,
                    value: NA_OPTION_VALUE,
                    id: BUILTIN_OPTION_ID
                };
                options = [...options, naOption];

                if (fullQuestionData.type === QTYPES.NUMERIC) {
                    this.setState({
                        numericOptions: {
                            ...this.state.numericOptions,
                            notApplicableOption: {
                                label: naOption.label,
                                value: naOption.value,
                                optionId: naOption.id,
                                sampleSize: { min: null, max: null },
                                mode: {
                                    terminateEnabled: false,
                                    quotaEnabled: false,
                                    ...mode
                                }
                            }
                        }
                    });
                }
            }
            if (fullQuestionData?.optionSettings?.showOther) {
                options = [
                    ...options,
                    {
                        label:
                            (
                                fullQuestionData.optionSettings.otherOption
                                    ?.texts?.[0]
                                    .contents?.[0] as PlainTextContent
                            )?.text || OTHER_DEFAULT_TEXT,
                        value: OTHER_OPTION_VALUE,
                        id: BUILTIN_OPTION_ID
                    }
                ];
            }

            if (
                fullQuestionData?.piping &&
                fullQuestionData?.pipingQuestions?.length
            ) {
                audienceRuleOptions = await this.getAudienceOptions(
                    {
                        questionId: fullQuestionData?.pipingQuestions[0].id
                    },
                    false,
                    originalQuestionType
                );
            }
            audienceRuleOptions = [
                ...audienceRuleOptions,
                ...options.map((o, i) => ({
                    optionId: o.id,
                    label: o.label,
                    value: o.value || o.id,
                    sampleSize: { min: null, max: null },
                    mode: {
                        terminateEnabled: false,
                        quotaEnabled: false,
                        ...mode
                    }
                }))
            ];
        } else {
            switch (question.type) {
                case QTYPES.VECTOR_MAP:
                    options = getVectorMapOptions(question);
                    break;
                case QTYPES.ICONS:
                    options = getIconScaleOptions(question);
                    break;
                case QTYPES.MULTIPLE_CHOICE:
                    mode = {
                        // Sets multiple options to unselected
                        ruleType: MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MAY_SELECT
                    };
                case QTYPES.LANGUAGE:
                    options = this.props.currentProject.languages.map((l) => ({
                        id: BUILTIN_OPTION_ID,
                        label: l.label,
                        value: l.optionValue,
                        optionKey: l.optionValue
                    }));
                    break;
                case QTYPES.PANEL:
                    options =
                        this.props.currentProject.panelSettings?.panels?.map(
                            (panel, i) => ({
                                id: BUILTIN_OPTION_ID,
                                label: panel.name || '(no name)',
                                value: i ? panel.key : 'DEFAULT',
                                optionKey: i ? panel.key : 'DEFAULT'
                            })
                        );
                    break;
                case QTYPES.DEVICE:
                    options = this.props.currentProject.devices.map((d) => ({
                        id: BUILTIN_OPTION_ID,
                        label: d,
                        value: DEVICE_OPTION_VALUE_PREFIX + d,
                        optionKey: DEVICE_OPTION_VALUE_PREFIX + d
                    }));
                    break;
                default:
                    options = (
                        this.props.filteredQuestionList as Question[]
                    ).find((q) => q.value == question.value).options;
                    break;
            }
            /** Add not applicable options */
            if (question.showNotApplicableOption) {
                const naOption = {
                    label:
                        (
                            question.notApplicableTexts?.[0]
                                ?.contents?.[0] as PlainTextContent
                        )?.text || NA_DEFAULT_TEXT,
                    value: NA_OPTION_VALUE,
                    id: BUILTIN_OPTION_ID
                };
                options = [...options, naOption];

                if (question.type === QTYPES.NUMERIC) {
                    this.setState({
                        numericOptions: {
                            ...this.state.numericOptions,
                            notApplicableOption: {
                                label: naOption.label,
                                value: naOption.value,
                                optionId: naOption.id,
                                sampleSize: { min: null, max: null },
                                mode: {
                                    terminateEnabled: false,
                                    quotaEnabled: false,
                                    ...mode
                                }
                            }
                        }
                    });
                }
            }
            audienceRuleOptions = (options || []).map((o) => {
                let terminateEnabled = true;
                if (
                    question.type === QTYPES.MULTIPLE_CHOICE ||
                    SYSTEM_METADATA_QTYPES.includes(question.type)
                ) {
                    terminateEnabled = false;
                }
                if (
                    o.value === BUILT_IN_OPTION_VALUE &&
                    this.props.keyName === AUDIENCE_RULE_TYPES.BUILT_IN_RULE
                ) {
                    terminateEnabled = false;
                }
                return {
                    optionId: o.id,
                    label: o.label,
                    value: o.value,
                    sampleSize: { min: null, max: null },
                    mode: {
                        // Sets options to unselected
                        terminateEnabled,
                        quotaEnabled: false,
                        ...mode
                    }
                };
            });
        }

        return audienceRuleOptions;
    };

    onChangeTargettingOption = (
        response: { label: string; value: string } | boolean,
        isMultipleAnswer: boolean,
        isBuiltIn: boolean,
        index: number
    ) => {
        const res = typeof response === 'boolean' ? response : response.value;
        const audienceRuleOptions = [
            ...this.props.audienceRule.audienceRuleOptions
        ];

        audienceRuleOptions[index].sampleSize = {
            min: null,
            max: null
        };
        if (isMultipleAnswer) {
            audienceRuleOptions[index].mode = {
                ...audienceRuleOptions[index].mode,
                ruleMode: MULTIPLE_ANSWER_AUDIENCE_RULE_MODE.ANY,
                ruleType: isBuiltIn
                    ? res
                        ? MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT
                        : MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MAY_SELECT
                    : res === 'Qualify'
                    ? MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT
                    : MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MAY_SELECT
            };
            isBuiltIn
                ? !res && delete audienceRuleOptions[index].mode.ruleMode
                : res === 'Disqualify' &&
                  delete audienceRuleOptions[index].mode.ruleMode;
        } else {
            audienceRuleOptions[index].mode.terminateEnabled = isBuiltIn
                ? !res
                    ? true
                    : false
                : res === 'Disqualify'
                ? true
                : false;
        }
        this.props.onRuleUpdate(
            this.props.index,
            {
                ...this.props.audienceRule,
                audienceRuleOptions
            },
            this.isValid
        );
    };

    onClickSelectAll = (selectAll: boolean, isMultipleAns: boolean) => {
        let audienceRuleOptions = this.props.audienceRule.audienceRuleOptions;

        if (selectAll) {
            audienceRuleOptions = audienceRuleOptions.map((quotaOption) => {
                if (quotaOption.value === BUILT_IN_OPTION_VALUE) {
                    return quotaOption;
                }
                if (isMultipleAns) {
                    return {
                        ...quotaOption,
                        mode: {
                            ...quotaOption.mode,
                            ruleMode: MULTIPLE_ANSWER_AUDIENCE_RULE_MODE.ANY,
                            ruleType:
                                MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT
                        }
                    };
                }
                return {
                    ...quotaOption,
                    mode: {
                        ...quotaOption.mode,
                        terminateEnabled: false
                    }
                };
            });
        } else {
            audienceRuleOptions = audienceRuleOptions.map((quotaOption) => {
                if (quotaOption.value === BUILT_IN_OPTION_VALUE) {
                    return quotaOption;
                }
                if (isMultipleAns) {
                    delete quotaOption.mode.ruleMode;
                    return {
                        ...quotaOption,
                        mode: {
                            ...quotaOption.mode,
                            ruleType:
                                MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MAY_SELECT
                        }
                    };
                }
                return {
                    ...quotaOption,
                    mode: {
                        ...quotaOption.mode,
                        terminateEnabled: true
                    },
                    sampleSize: { min: null, max: null }
                };
            });
        }

        this.props.onRuleUpdate(
            this.props.index,
            {
                ...this.props.audienceRule,
                audienceRuleOptions
            },
            this.isValid
        );
    };

    onChangeFilterToggle = () => {
        const toggle = this.props.audienceRule.audienceRuleOptions
            .filter((opt) => opt.value !== BUILT_IN_OPTION_VALUE)
            .every((opt) => opt.mode.quotaEnabled);
        const audienceRuleOptions =
            this.props.audienceRule.audienceRuleOptions.map((option) => ({
                ...option,
                mode: {
                    ...option.mode,
                    quotaEnabled:
                        option.value === BUILT_IN_OPTION_VALUE ? false : !toggle
                }
            }));
        this.props.onRuleUpdate(
            this.props.index,
            {
                ...this.props.audienceRule,
                audienceRuleOptions
            },
            this.isValid
        );
    };

    onChangeMultipleAnswerMode = (
        ruleMode: MULTIPLE_ANSWER_AUDIENCE_RULE_MODE
    ) => {
        const audienceRuleOptions =
            this.props.audienceRule.audienceRuleOptions.map((opt) => {
                const ruleType = opt.mode?.ruleType;
                // We don't allow na with MAY_SELECT when rule type for MUST_SELECT is all
                if (
                    opt.value === NA_OPTION_VALUE &&
                    ruleMode === MULTIPLE_ANSWER_AUDIENCE_RULE_MODE.ALL
                ) {
                    return {
                        ...opt,
                        mode: { terminateEnabled: true, quotaEnabled: false }
                    };
                } else if (
                    ruleType === MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT
                ) {
                    return { ...opt, mode: { ...opt.mode, ruleMode } };
                }
                return opt;
            });

        this.props.onRuleUpdate(
            this.props.index,
            { ...this.props.audienceRule, audienceRuleOptions },
            this.isValid
        );
    };

    onChangeMultipleAnswerRadioButton = (
        index: number,
        ruleType: MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES,
        ruleMode: MULTIPLE_ANSWER_AUDIENCE_RULE_MODE
    ) => {
        const currentAudienceRuleOption = {
            ...this.props.audienceRule.audienceRuleOptions[index]
        };
        const terminateEnabled =
            ruleType === MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.DISQUALIFY;
        currentAudienceRuleOption.mode = {
            ...currentAudienceRuleOption.mode,
            ruleType,
            ruleMode,
            terminateEnabled
        };

        terminateEnabled && delete currentAudienceRuleOption.mode.ruleType;
        !ruleMode && delete currentAudienceRuleOption.mode.ruleMode;
        const audienceRule = { ...this.props.audienceRule };
        audienceRule.audienceRuleOptions.splice(
            index,
            1,
            currentAudienceRuleOption
        );

        this.props.onRuleUpdate(this.props.index, audienceRule, this.isValid);
    };

    onCancel = () => {
        this.setState({
            showError: false,
            hasInvalid: false,
            errorObject: null
        });
        if (this.props.audienceRule.selectedQuestion.type === QTYPES.NUMERIC) {
            this.setState({ numericOptions: this._numericOptions });
        }

        this.props.onCancel(this.props.index);
    };

    onSave = () => {
        if (this.state.hasInvalid) {
            this.setState({
                showError: true
            });
        } else {
            this.props.onSave(this.props.index);
        }
    };

    onEdit = (editingQuestion) => {
        if (!this.props.disableByFieldName('edit-custom-screener')) {
            this.props.onEdit(this.props.index);
            this.onQuestionChange(editingQuestion);
            this.setState({
                showError: false,
                isExpandedAudienceRule: true
            });
            this.onScrollToView();
        }
    };

    onDelete = () => {
        if (this.props.editEnabled) {
            this.props.onDelete(this.props.index);
        }
    };

    isValid = () => {
        const { validationService } = this.props;
        const { selectedQuestion } = this.props.audienceRule;
        const isMultipleAns = MULTIPLE_ANSWER_TYPES.includes(
            selectedQuestion.type
        );
        const audienceRuleOptions = [];
        if (selectedQuestion.value === DYNATA_QVALUE.AGE) {
        } else {
            audienceRuleOptions.push(
                ...this.props.audienceRule.audienceRuleOptions
            );
        }

        const errorObject = {
            errorMessage: '',
            optionKeys: {}
        };
        let hasInvalid = false;
        const toggleEnabled = audienceRuleOptions.some(
            (opt) => opt.mode.quotaEnabled
        );

        // validate at least one item is set to go through
        const isNumValid = audienceRuleOptions
            .filter(
                (audienceRuleOption) =>
                    audienceRuleOption.value !== BUILT_IN_OPTION_VALUE
            )
            .some((audienceRuleOption) => {
                if (isMultipleAns) {
                    return (
                        audienceRuleOption.mode.ruleType ===
                        MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT
                    );
                } else {
                    return !audienceRuleOption.mode.terminateEnabled;
                }
            });
        if (!isNumValid) {
            errorObject.errorMessage = 'At least one item should be checked';
            hasInvalid = true;
            this.setState({
                hasInvalid,
                errorObject
            });
            return;
        } else if (!toggleEnabled) {
            this.setState({
                hasInvalid,
                errorObject
            });
            return;
        }
        audienceRuleOptions.forEach((opt, index) => {
            if (
                !opt.mode.terminateEnabled &&
                validationService.isEmpty(opt.sampleSize.max)
            ) {
                hasInvalid = true;
                errorObject.optionKeys[index] = true;
                if (errorObject.errorMessage === '') {
                    errorObject.errorMessage =
                        'Quota field cannot be left empty!';
                }
            }
        });

        this.setState({
            hasInvalid,
            errorObject
        });
    };

    getQuestion = async (questionId): Promise<Question> => {
        try {
            const response: any = await this.props.getQuestionByQuestionId(
                questionId,
                { quotas: true }
            );
            return response;
        } catch (e) {
            return {} as Question;
        }
    };

    setIsMenuOpen = () => {
        this.setState({
            isMenuOpen: this.state.isMenuOpen ? false : true
        });
    };

    /** BEGINNING of NUMERIC question functions */

    /**
     * Initialize numeric range options
     */
    initializeNumericOptions = (audienceRule: IAudienceRule) => {
        const { audienceRuleOptions } = audienceRule;
        const numericOptions = {
            rangeOptions: [],
            beforeRangeOption: null,
            afterRangeOption: null,
            notApplicableOption: null
        };
        audienceRuleOptions.forEach(
            (audienceRuleOption: AudienceRuleOption) => {
                if (audienceRuleOption.value === NUMERIC_BEFORE_RANGE_VALUE) {
                    numericOptions.beforeRangeOption = audienceRuleOption;
                } else if (
                    audienceRuleOption.value === NUMERIC_AFTER_RANGE_VALUE
                ) {
                    numericOptions.afterRangeOption = audienceRuleOption;
                } else if (audienceRuleOption.value === NA_OPTION_VALUE) {
                    numericOptions.notApplicableOption = audienceRuleOption;
                } else {
                    numericOptions.rangeOptions.push(audienceRuleOption);
                }
            }
        );
        this._numericOptions = _.cloneDeep(numericOptions);
        this.setState({ numericOptions });
    };

    /**
     * Add a new numeric range option and validate it
     */
    addNumericOption = (quotaEnabled = false): void => {
        const { numericOptions } = this.state;
        const nextPosition =
            numericOptions.rangeOptions.length === 0
                ? 0
                : numericOptions.rangeOptions.length;
        const newOption = {
            label: '',
            mode: {
                terminateEnabled: false,
                quotaEnabled
            },
            optionId: -1,
            sampleSize: {
                min: null,
                max: null
            },
            value: String(nextPosition),
            range: { min: null, max: null }
        };
        const updatedOptions = [...numericOptions.rangeOptions, newOption];

        this.setState(
            {
                numericOptions: {
                    ...numericOptions,
                    rangeOptions: updatedOptions
                }
            },
            () => {
                this.validateNumeric(this.state.numericOptions, quotaEnabled);
            }
        );
    };
    /**
     * By default gap is set to 1
     * if the numeric question has "allowDecimal" enabled and a custom numDecimals other than 0, the gap should change accordingly and it's always 10^-n
     * i.e. 2 decimal points -> 0.01
     * @returns number
     */
    getGapByDecimalPoints = (): number => {
        let gap = 1;

        const { selectedQuestionSummary } = this.state;
        const { audienceSettings } = selectedQuestionSummary || {};

        if (this.getAllowedDecimalPoints()) {
            gap =
                gap /
                Math.pow(10, audienceSettings?.openEndedSettings?.numDecimals);
        }
        return gap;
    };

    /**
     * @returns numDeicmals if the question has allowDecimal enabled, otherwise returns 0
     */
    getAllowedDecimalPoints = (): number => {
        const { audienceSettings } = this.state.selectedQuestionSummary || {};
        return audienceSettings?.openEndedSettings?.allowDecimal
            ? audienceSettings?.openEndedSettings?.numDecimals
            : 0;
    };

    /**
     * Edit numeric range options and validate the range
     */
    onEditNumericOptions = (numericOptions, quotaEnabled: boolean): void => {
        this.setState({ numericOptions }, () => {
            if (numericOptions.rangeOptions.length) {
                this.validateNumeric(numericOptions, quotaEnabled);
            }
        });
    };

    validateNumeric = (numericOptions, quotaEnabled: boolean) => {
        let { errorObject } = this.state;
        const { selectedQuestionSummary } = this.state;
        let hasInvalid = false;

        const isInvalidWithMinAndMax = (value) => {
            let isInvalid = false;
            if (
                selectedQuestionSummary?.audienceSettings?.openEndedSettings
                    ?.showMin &&
                value < selectedQuestionSummary?.audienceSettings?.min
            ) {
                isInvalid = true;
            }
            if (
                selectedQuestionSummary?.audienceSettings?.openEndedSettings
                    ?.showMax &&
                value > selectedQuestionSummary?.audienceSettings?.max
            ) {
                isInvalid = true;
            }
            return isInvalid;
        };

        const numericValidation = numericOptions.rangeOptions.map(
            (rangeOption, i) => {
                const { range } = rangeOption;
                const validation = {
                    min: { message: '', isInvalid: false },
                    max: { message: '', isInvalid: false }
                };

                // Validate Range
                const maxValidator =
                    this.props.validationService?.isEmpty(range.max) ||
                    Number.isNaN(range.max);
                const minValidator =
                    this.props.validationService?.isEmpty(range.min) ||
                    Number.isNaN(range.min);

                // Max value is empty or not a number
                if (maxValidator) {
                    validation.max = {
                        isInvalid: true,
                        message: 'The range cannot be empty'
                    };
                    hasInvalid = true;
                }

                // Min value is empty or not a number
                if (minValidator) {
                    validation.min = {
                        isInvalid: true,
                        message: 'The range cannot be empty'
                    };
                    hasInvalid = true;
                }

                if (!maxValidator && !minValidator && range.min > range.max) {
                    validation.min = {
                        isInvalid: true,
                        message: 'The range looks incorrect'
                    };
                    validation.max = {
                        isInvalid: true,
                        message: ''
                    };
                    hasInvalid = true;
                }

                if (
                    _.isNumber(range.min) &&
                    isInvalidWithMinAndMax(range.min)
                ) {
                    validation.min = {
                        isInvalid: true,
                        message: `The range should be between ${selectedQuestionSummary?.audienceSettings?.min} and ${selectedQuestionSummary?.audienceSettings?.max}`
                    };
                    hasInvalid = true;
                }
                if (
                    _.isNumber(range.max) &&
                    isInvalidWithMinAndMax(range.max)
                ) {
                    validation.max = {
                        isInvalid: true,
                        message: `The range should be between ${selectedQuestionSummary?.audienceSettings?.min} and ${selectedQuestionSummary?.audienceSettings?.max}`
                    };
                    hasInvalid = true;
                }

                return validation;
            }
        );
        errorObject = { ...errorObject, ageRange: numericValidation };
        this.validateRange(
            numericOptions,
            hasInvalid,
            errorObject,
            quotaEnabled
        );
    };

    /**
     * Validate numeric range and generate remaining options(first and last options) if the numeric range is valid
     */
    validateRange = (numericOptions, hasInvalid, errorObject, quotaEnabled) => {
        const ranges = numericOptions.rangeOptions.map(
            (rangeOption, i) => rangeOption.range
        );
        const gap = this.getGapByDecimalPoints();

        ranges.forEach((range, i) => {
            const { min, max } = range;
            const nextRange = ranges?.[i + 1];

            /** The difference between min value of nextRange and max value of currentRange should be same as gap */
            if (nextRange) {
                const { min: nextMin } = nextRange;

                const diff = _.round(
                    nextMin - max,
                    this.getAllowedDecimalPoints()
                );

                if (
                    !this.props.validationService.isEmpty(nextMin) &&
                    diff !== gap
                ) {
                    hasInvalid = true;
                    errorObject = {
                        ...errorObject,
                        errorMessage: `The gap between ${max} and ${nextMin} looks incorrect`
                    };
                }
            }
        });

        this.setState({ hasInvalid, errorObject });

        if (!hasInvalid) {
            this.generateRemainingRanges(numericOptions, quotaEnabled);
        }
    };

    /**
     * Generate remaining ranges
     * firstNumericRange
     * - usually it's "smaller than N" where N is the min value of the first range option
     * - it is skipped when question.min and the min value of the first range option is same
     * lastNumericRange
     * - usually it's "bigger than N" where N is the max value of the last range option
     * - it is skipped when question.max and the max value of the last range option is same
     */
    generateRemainingRanges = (numericOptions, quotaEnabled = false) => {
        const { selectedQuestionSummary } = this.state;
        const min =
            _.isNumber(selectedQuestionSummary?.audienceSettings?.min) &&
            selectedQuestionSummary?.audienceSettings?.openEndedSettings
                ?.showMin
                ? selectedQuestionSummary?.audienceSettings?.min
                : null;
        const max =
            _.isNumber(selectedQuestionSummary?.audienceSettings?.max) &&
            selectedQuestionSummary?.audienceSettings?.openEndedSettings
                ?.showMax
                ? selectedQuestionSummary?.audienceSettings?.max
                : null;
        const newRangeOption = {
            label: '',
            mode: {
                terminateEnabled:
                    this.state.selectedQuestionSummary?.builtInID ===
                    DYNATA_QVALUE.AGE
                        ? true
                        : false,
                quotaEnabled
            },
            optionId: -1,
            sampleSize: {
                min: null,
                max: null
            },
            value: null,
            range: { min: null, max: null }
        };
        let beforeRangeOption, afterRangeOption;
        const gap = this.getGapByDecimalPoints();

        const firstNumericRange = numericOptions.rangeOptions?.[0].range.min;
        const lastNumericRange =
            numericOptions.rangeOptions?.[
                numericOptions.rangeOptions.length - 1
            ].range.max;

        const beforeFirstNumericRange = _.round(
            firstNumericRange - gap,
            this.getAllowedDecimalPoints()
        );
        if (min !== beforeFirstNumericRange && min !== firstNumericRange) {
            beforeRangeOption = {
                ...(numericOptions.beforeRangeOption ||
                    _.cloneDeep(newRangeOption)),
                range: { min, max: beforeFirstNumericRange },
                label: min
                    ? `${min} to ${beforeFirstNumericRange}`
                    : `Smaller than ${beforeFirstNumericRange}`,
                value: NUMERIC_BEFORE_RANGE_VALUE
            };
        }

        const afterLastNumericRange = _.round(
            lastNumericRange + gap,
            this.getAllowedDecimalPoints()
        );
        if (afterLastNumericRange !== max && max !== lastNumericRange) {
            afterRangeOption = {
                ...(numericOptions.afterRangeOption ||
                    _.cloneDeep(newRangeOption)),
                range: { min: afterLastNumericRange, max },
                label: max
                    ? `${afterLastNumericRange} to ${max}`
                    : `Bigger than ${afterLastNumericRange}`,
                value: NUMERIC_AFTER_RANGE_VALUE
            };
        }

        this.setState((prevState) => ({
            numericOptions: {
                ...prevState.numericOptions,
                beforeRangeOption,
                afterRangeOption
            }
        }));
    };

    onDeleteNumericOption = (index: number, quotEnabled: boolean): void => {
        const { numericOptions } = this.state;
        const numericRangeOptions = [
            ...numericOptions.rangeOptions.slice(0, index),
            ...numericOptions.rangeOptions.slice(index + 1)
        ];
        const clearAutoGeneratedOptions = !numericRangeOptions.length;
        this.setState(
            (prevState) => ({
                ...prevState,
                numericOptions: {
                    ...prevState.numericOptions,
                    rangeOptions: numericRangeOptions,
                    beforeRangeOption: clearAutoGeneratedOptions
                        ? null
                        : prevState.numericOptions.beforeRangeOption,
                    afterRangeOption: clearAutoGeneratedOptions
                        ? null
                        : prevState.numericOptions.afterRangeOption
                }
            }),
            () => {
                if (numericRangeOptions.length)
                    this.validateNumeric(
                        this.state.numericOptions,
                        quotEnabled
                    );
            }
        );
    };

    /**
     * Format this.state.numericOptions to AudienceRuleOption
     */
    addNumericToAudienceRule = () => {
        const { numericOptions } = this.state;
        const audienceRuleOptions: AudienceRuleOption[] = [];

        if (numericOptions.beforeRangeOption) {
            audienceRuleOptions.push(numericOptions.beforeRangeOption);
        }
        if (numericOptions.rangeOptions.length) {
            const addLabelToRangeOptions = (rangeOptions) => {
                return rangeOptions.map((rangeOption) => ({
                    ...rangeOption,
                    label: `${rangeOption.range.min || ''} to ${
                        rangeOption.range.max || ''
                    }`
                }));
            };
            audienceRuleOptions.push(
                ...addLabelToRangeOptions(numericOptions.rangeOptions)
            );
        }
        if (numericOptions.afterRangeOption) {
            audienceRuleOptions.push(numericOptions.afterRangeOption);
        }
        if (numericOptions.notApplicableOption) {
            audienceRuleOptions.push(numericOptions.notApplicableOption);
        }

        this.props.onRuleUpdate(this.props.index, {
            ...this.props.audienceRule,
            audienceRuleOptions
        });
    };
    /** End of NUMERIC question functions */

    /**
     * Renders custom screeners and built in targeting options with
     * question type === multiple_choice
     */
    renderAudienceBody = (toggle: boolean, halfWidth: boolean) => {
        const { audienceRule } = this.props;
        const { selectedQuestion } = audienceRule;
        const audienceRuleOptions = [...audienceRule.audienceRuleOptions];
        let passedAudienceOptions;

        // check if NA option exists -> return index
        const NAIndex = audienceRuleOptions.findIndex(
            (item) => item.value === NA_OPTION_VALUE
        );

        // Check if NA is the last option in audienceRuleOptions -> if not shift to end
        if (
            NAIndex !== -1 &&
            NAIndex !== audienceRule.audienceRuleOptions.length - 1
        ) {
            audienceRuleOptions.push(audienceRuleOptions.splice(NAIndex, 1)[0]);
        }

        // If question is full width split into 2 arrays, left and right
        if (!halfWidth) {
            let middle;
            audienceRuleOptions.length % 2 !== 0
                ? (middle = (audienceRuleOptions.length + 1) / 2)
                : (middle = audienceRuleOptions.length / 2);
            passedAudienceOptions = [
                audienceRuleOptions.slice(0, middle),
                audienceRuleOptions.slice(middle)
            ];
        } else {
            passedAudienceOptions = [...audienceRuleOptions];
        }
        if (this.state.isLoading) {
            return <Loader />;
        }

        if (selectedQuestion) {
            switch (true) {
                case QTYPES.NUMERIC === selectedQuestion.type:
                    return (
                        <NumericAudienceRule
                            audienceRule={audienceRule}
                            isMenuOpen={this.state.isMenuOpen}
                            isDisabled={!this.props.audienceRule.edit}
                            totalSampleSize={this.props.totalSampleSize}
                            hasInvalid={this.state.hasInvalid}
                            errorObject={this.state.errorObject}
                            health={this.props.health}
                            numericOptions={this.state.numericOptions}
                            setIsMenuOpen={this.setIsMenuOpen}
                            onEdit={this.onEdit}
                            onDelete={this.onDelete}
                            onAddNewOption={this.addNumericOption}
                            disableByFieldName={this.props.disableByFieldName}
                            onEditOptions={this.onEditNumericOptions}
                            onDeleteOption={this.onDeleteNumericOption}
                            selectedQuestionSummary={
                                this.state.selectedQuestionSummary
                            }
                        />
                    );
                case MULTIPLE_ANSWER_TYPES.includes(selectedQuestion.type):
                    return (
                        <MultipleAudienceRule
                            audienceRule={audienceRule}
                            audienceRuleOptions={passedAudienceOptions}
                            halfWidth={halfWidth}
                            isMenuOpen={this.state.isMenuOpen}
                            isDisabled={!this.props.audienceRule.edit}
                            setIsMenuOpen={this.setIsMenuOpen}
                            onEdit={this.onEdit}
                            onDelete={this.onDelete}
                            onChangeMultipleAnswerMode={
                                this.onChangeMultipleAnswerMode
                            }
                            onChangeMultipleAnswerRadioButton={
                                this.onChangeMultipleAnswerRadioButton
                            }
                            disableByFieldName={this.props.disableByFieldName}
                            health={this.props.health}
                        />
                    );
                default:
                    return (
                        <SingleAudienceRule
                            audienceRule={audienceRule}
                            audienceRuleOptions={passedAudienceOptions}
                            halfWidth={halfWidth}
                            toggle={toggle}
                            isDisabled={!this.props.audienceRule.edit}
                            hasInvalid={this.state.hasInvalid}
                            errorObject={this.state.errorObject}
                            isMenuOpen={this.state.isMenuOpen}
                            setIsMenuOpen={this.setIsMenuOpen}
                            onEdit={this.onEdit}
                            onDelete={this.onDelete}
                            onChangeFilterToggle={this.onChangeFilterToggle}
                            onChangeSampleSize={this.onSampleSizeChange}
                            onChangeTargettingOption={
                                this.onChangeTargettingOption
                            }
                            onClickSelectAll={this.onClickSelectAll}
                            disableByFieldName={this.props.disableByFieldName}
                            health={this.props.health}
                        />
                    );
            }
        } else {
            return null;
        }
    };
    renderBuiltInAudienceBody = (toggle: boolean) => {
        const { audienceRule, currentProject } = this.props;

        const isMultipleAns = MULTIPLE_ANSWER_TYPES.includes(
            audienceRule.selectedQuestion.type as QTYPES
        );
        switch (audienceRule.selectedQuestion.value as string) {
            case DYNATA_QVALUE.AGE:
                return (
                    <NumericAudienceRule
                        audienceRule={audienceRule}
                        isMenuOpen={this.state.isMenuOpen}
                        isDisabled={!this.props.audienceRule.edit}
                        totalSampleSize={this.props.totalSampleSize}
                        hasInvalid={this.state.hasInvalid}
                        errorObject={this.state.errorObject}
                        health={this.props.health}
                        numericOptions={this.state.numericOptions}
                        setIsMenuOpen={this.setIsMenuOpen}
                        onEdit={this.onEdit}
                        onDelete={this.onDelete}
                        onAddNewOption={this.addNumericOption}
                        disableByFieldName={this.props.disableByFieldName}
                        onEditOptions={this.onEditNumericOptions}
                        onDeleteOption={this.onDeleteNumericOption}
                        selectedQuestionSummary={
                            this.state.selectedQuestionSummary
                        }
                        isBuiltIn
                    />
                );

            default:
                return (
                    <BuiltInAudienceRule
                        audienceRule={audienceRule}
                        toggle={toggle}
                        isDisabled={!this.props.audienceRule.edit}
                        isMenuOpen={this.state.isMenuOpen}
                        hasInvalid={this.state.hasInvalid}
                        errorObject={this.state.errorObject}
                        totalSampleSize={this.props.totalSampleSize}
                        currentProject={currentProject}
                        setIsMenuOpen={this.setIsMenuOpen}
                        onEdit={this.onEdit}
                        onDelete={this.onDelete}
                        onChangeFilterToggle={this.onChangeFilterToggle}
                        onChangeTargettingOption={this.onChangeTargettingOption}
                        onChangeSampleSize={this.onSampleSizeChange}
                        onClickSelectAll={this.onClickSelectAll}
                        disableByFieldName={this.props.disableByFieldName}
                        health={this.props.health}
                    />
                );
        }
    };
    render() {
        const { keyName, audienceRule } = this.props;
        const isBuiltIn = keyName === AUDIENCE_RULE_TYPES.BUILT_IN_RULE;
        const toggle = audienceRule?.audienceRuleOptions?.some(
            (opt) => opt.mode?.quotaEnabled
        );
        let halfWidth = false;
        if (audienceRule.audienceRuleOptions.length < 6) {
            !isBuiltIn && (halfWidth = true);
            audienceRule.selectedQuestion.value === DYNATA_QVALUE.AGE &&
                (halfWidth = true);
        }

        return (
            <div
                className={`audience--screeners--card ${
                    this.props.index === -1 ? 'new-card' : ''
                }`}
                style={{
                    width: halfWidth ? '49%' : '99%'
                }}
                onMouseLeave={() => this.setState({ isMenuOpen: false })}
                ref={this.myRef}
            >
                {!isBuiltIn
                    ? this.renderAudienceBody(toggle, halfWidth)
                    : this.renderBuiltInAudienceBody(toggle)}

                {this.props.audienceRule.edit && (
                    <div className="btn-footer">
                        {this.state.hasInvalid && (
                            <div
                                className="audience-error-message"
                                style={{
                                    justifyContent: !isBuiltIn
                                        ? 'flex-start'
                                        : 'end'
                                }}
                            >
                                {this.state.errorObject.errorMessage}
                            </div>
                        )}
                        <button
                            className="btn btn--sm btn--transparent"
                            onClick={this.onCancel}
                        >
                            Cancel
                        </button>
                        <button
                            className={classnames('btn btn--sm btn--green', {
                                disabled: this.state.isLoading
                            })}
                            disabled={
                                this.state.isLoading || this.state.hasInvalid
                            }
                            onClick={this.onSave}
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = ({ projectReducer }: IApplicationState) => ({
    languages: projectReducer.currentProject.languages,
    editEnabled: projectReducer.editEnabled['audience']
});

const mapDispatchToProps = (dispatch) => ({
    getQuestionByQuestionId: (id: number, options: any) =>
        dispatch(getQuestionByQuestionId(id, options))
});

export default connect(mapStateToProps, mapDispatchToProps)(AudienceRule);
